import { useHistory } from "react-router-dom";
import { Stack } from "@mui/material";
import List from "@mui/material/List";

import { AmountInCustomerCurrency } from "../AmountInCustomerCurrency";
import { CardPager } from "../CardPager";
import EmptyList from "../EmptyList";
import HomePageCard from "../HomePageCard";
import { BudgetRow } from "../Rows/BudgetRow";
import { type ParsedBudget } from "../types";
import { ITEMS_PER_PAGE, usePagination } from "./hooks";

type ActiveBudgetsListProps = {
  budgets: ParsedBudget[];
  budgetsAtRisk: number;
};

export const ActiveBudgetsList = ({ budgets, budgetsAtRisk }: ActiveBudgetsListProps) => {
  const history = useHistory();
  const { currentPageItems, currentPage, startItem, endItem, totalItems, handlePageChange } = usePagination(budgets);

  if (budgets.length === 0) {
    return (
      <EmptyList
        title="No budgets to action yet"
        subheader="Reduce risk by taking action and acknowledging anomalies"
        description="When budgets are created you will be able to track them here and take action to reduce waste and risk "
        buttonText="Create a budget"
        buttonDataCy="implemented-budgets-btn"
        onButtonClick={() => {
          history.push("./monitor/budgets/create");
        }}
      />
    );
  }

  return (
    <HomePageCard
      title="Active Budgets"
      subheader={
        budgetsAtRisk > 0 ? (
          <>
            Budgets are nearing or exceeding their total amounts with a{" "}
            <b>
              <AmountInCustomerCurrency amount={budgetsAtRisk} dontConvert />
            </b>{" "}
            overrun forecasted
          </>
        ) : (
          "Active budgets by nearest to total budget amount"
        )
      }
      sxProps={{ height: "100%" }}
    >
      <Stack sx={{ flex: 1, justifyContent: "space-between" }}>
        <List dense sx={{ pb: 0, pt: 0, "& .MuiListItem-root:last-of-type": { borderBottom: "none" } }}>
          {currentPageItems.map((budget, index) => (
            <BudgetRow
              key={budget.id}
              budget={budget}
              hideDivider={index === currentPageItems.length - 1 && currentPageItems.length === ITEMS_PER_PAGE}
            />
          ))}
        </List>
        <CardPager
          startItem={startItem}
          endItem={endItem}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </Stack>
    </HomePageCard>
  );
};
